<script lang="ts" setup>
import { ref, computed, watch, onMounted } from "vue";
import { useUserStore } from "@/stores/user";
import { useFiltertokenStore } from "@/stores/filtertoken";
import { getAvailableWidgets, getUserWidgets, updateUserWidgets, severityCode } from "@/services/api/dashboard.api";
import { useToast } from "vue-toastification";
import DataAvailability from "@/components/general/DataAvailability.vue";
import ComplexFunctions from "@/components/app/homes/ComplexFunctions.vue";
import BuildingEnergylabelStatus from "@/components/app/buildings/BuildingEnergyLabelStatus.vue";
import SustainabilityRadar from "@/components/app/charts/SustainabilityRadar.vue";
import HomesHeatingTypes from "@/components/app/homes/HomesHeatingTypes.vue";
import BuildingMeasures from "@/components/app/buildings/BuildingMeasures.vue";
import NetHeatRequirement from "@/components/general/NetHeatRequirement.vue";
const userStore = useUserStore();
const filtertokenStore = useFiltertokenStore();
const toaster = useToast();

const refDate = ref(new Date()); // Kan later gewijzigd worden mbv een tijdlijn

type DashboardWidgetDefinition = {
  key: string;
  title: string;
  description: string;
  icon: string;
};

const widgetsMenuOpen = ref(false);
const saveWidgetsBusy = ref(false);
const availableWidgets = ref<DashboardWidgetDefinition[]>([]);
const selectedWidgetKeys = ref<string[]>([]);
const tooltipsDisabled = ref(false);

const partyId = computed(() => userStore.currentPartyReference);
const userId = computed(() => userStore.currentUserId);
const orderedAvailableWidgetKeys = computed(() => availableWidgets.value.map((x) => x.key));
const dashboardPartyType = "Homes";

const token = computed(() => {
  if (!filtertokenStore.projectToken) {
    loadProjectToken();
  }
  return filtertokenStore.projectToken;
});

async function loadProjectToken() {
  await filtertokenStore.getProjectToken();
}

function isWidgetSelected(widgetKey: string) {
  return selectedWidgetKeys.value.includes(widgetKey);
}

function toOrderedKnownKeys(keys: string[]) {
  const knownKeys = new Set(orderedAvailableWidgetKeys.value);
  return keys.filter((key) => knownKeys.has(key));
}

function normalizeAvailableWidgets(data: any[]): DashboardWidgetDefinition[] {
  return data
    .filter((x) => typeof x?.key === "string" && x.key.length > 0 && x.key !== "Co2Savings")
    .map((x) => ({
      key: x.key,
      title: x.title || x.key,
      description: x.description || "",
      icon: x.icon || "mdi-view-dashboard-outline",
    }));
}

async function loadDashboardWidgets() {
  if (!userId.value) {
    return;
  }

  const availableResponse = await getAvailableWidgets(dashboardPartyType);
  if (availableResponse.severity <= severityCode.warning && Array.isArray(availableResponse.data)) {
    availableWidgets.value = normalizeAvailableWidgets(availableResponse.data);
  } else if (availableResponse.severity >= severityCode.error) {
    availableWidgets.value = [];
    toaster.error("Ophalen van beschikbare dashboard widgets is mislukt.");
  } else {
    availableWidgets.value = [];
  }

  const userWidgetsResponse = await getUserWidgets(partyId.value, userId.value);
  if (userWidgetsResponse.severity <= severityCode.warning) {
    const widgets = Array.isArray(userWidgetsResponse.data?.widgets) ? [...userWidgetsResponse.data.widgets] : [];
    const configuredKeys = widgets.sort((a, b) => a.sequence - b.sequence).map((x) => x.widgetName);
    const knownKeys = toOrderedKnownKeys(configuredKeys);
    selectedWidgetKeys.value = knownKeys;
  } else {
    selectedWidgetKeys.value = [];
    toaster.error("Ophalen van dashboard instellingen is mislukt.");
  }
}

async function saveDashboardWidgets() {
  if (!partyId.value || !userId.value) {
    return;
  }

  const payload = selectedWidgetKeys.value.map((widgetName, sequence) => ({
    widgetName,
    sequence,
  }));

  saveWidgetsBusy.value = true;
  const response = await updateUserWidgets(partyId.value, userId.value, payload);
  saveWidgetsBusy.value = false;

  if (response.severity >= severityCode.error) {
    toaster.error("Opslaan van dashboard instellingen is mislukt.");
  }
}

async function toggleWidget(widgetKey: string) {
  const selected = new Set(selectedWidgetKeys.value);
  if (selected.has(widgetKey)) {
    selected.delete(widgetKey);
  } else {
    selected.add(widgetKey);
  }

  selectedWidgetKeys.value = toOrderedKnownKeys(Array.from(selected));
  await saveDashboardWidgets();
}

let draggedIndex: number | null = null;

function handleCardDragStart(event: DragEvent, index: number) {
  tooltipsDisabled.value = true;
  draggedIndex = index;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
  }
}

function handleCardDragOver(event: DragEvent) {
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "move";
  }
}

function handleCardDrop(event: DragEvent, dropIndex: number) {
  event.preventDefault();
  if (draggedIndex !== null && draggedIndex !== dropIndex) {
    const items = [...selectedWidgetKeys.value];
    const draggedItem = items[draggedIndex];
    items.splice(draggedIndex, 1);
    items.splice(dropIndex, 0, draggedItem);
    selectedWidgetKeys.value = items;
    saveDashboardWidgets();
  }
  draggedIndex = null;
  window.requestAnimationFrame(() => {
    tooltipsDisabled.value = false;
  });
}

function handleCardDragEnd() {
  draggedIndex = null;
  window.requestAnimationFrame(() => {
    tooltipsDisabled.value = false;
  });
}

async function removeWidget(widgetKey: string) {
  await toggleWidget(widgetKey);
}

onMounted(async () => {
  await userStore.checkStoreState();
  await loadDashboardWidgets();
});

watch(
  () => userStore.currentPartyReference,
  async (newRef, oldRef) => {
    if (!newRef || newRef === oldRef) return;
    await loadDashboardWidgets();
  }
);
</script>

<template>
  <BasePage>
    <div class="app-dashboard-overview">
      <v-container fluid class="pa-0">
        <v-row class="align-center">
          <v-col cols="12" md="8" class="app-welcome-message">
            <h1>Project Dashboard</h1>
            <p>Bekijk je belangrijkste statistieken en kritische prestatie-indicatoren (KPI's)</p>
          </v-col>
          <v-col cols="12" md="4" class="d-flex justify-md-end justify-start mt-2 mt-md-0">
            <v-menu v-model="widgetsMenuOpen" location="bottom end" :close-on-content-click="false">
              <template #activator="{ props }">
                <v-btn v-bind="props" prepend-icon="mdi-cog-outline" variant="outlined">Dashboard instellen</v-btn>
              </template>

              <v-card min-width="340">
                <v-card-text class="pb-2">Beheer hier de widgets die op het dashboard worden getoond.</v-card-text>
                <v-divider></v-divider>
                <v-list density="compact">
                  <v-list-item v-for="widget in availableWidgets" :key="widget.key" :disabled="saveWidgetsBusy" @click="toggleWidget(widget.key)">
                    <template #prepend>
                      <v-icon :icon="widget.icon"></v-icon>
                    </template>
                    <v-list-item-title>{{ widget.title }}</v-list-item-title>
                    <v-list-item-subtitle>{{ widget.description }}</v-list-item-subtitle>
                    <template #append>
                      <v-icon v-if="isWidgetSelected(widget.key)" icon="mdi-check-circle" color="success"></v-icon>
                    </template>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <div class="app-dashboard-widget-overview">
              <template v-for="(widgetKey, index) in selectedWidgetKeys" :key="widgetKey">
                <article v-if="widgetKey === 'NetHeatRequirement'" class="widget dashboard-card dashboard-card--small dashboard-score-card dashboard-card--net-heat dashboard-card-wrapper" :class="{ 'is-dragging': draggedIndex === index }" draggable="true" @dragstart="handleCardDragStart($event, index)" @dragover="handleCardDragOver($event)" @drop="handleCardDrop($event, index)" @dragend="handleCardDragEnd">
                  <div class="dashboard-card-overlay">
                    <v-tooltip text="Sleep om te verplaatsen" location="bottom" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-drag-vertical" class="drag-handle-center"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <div class="dashboard-card-remove">
                    <v-tooltip text="Verwijderen" location="start" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-close" class="remove-btn-right" @click="removeWidget(widgetKey)"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <net-heat-requirement v-if="token" :token="token" :refDate="refDate" :compact="true"></net-heat-requirement>
                </article>

                <article v-if="widgetKey === 'DataQualityHomes' || widgetKey === 'DataQuality'" class="widget dashboard-card dashboard-card--small dashboard-card-wrapper" :class="{ 'is-dragging': draggedIndex === index }" draggable="true" @dragstart="handleCardDragStart($event, index)" @dragover="handleCardDragOver($event)" @drop="handleCardDrop($event, index)" @dragend="handleCardDragEnd">
                  <div class="dashboard-card-overlay">
                    <v-tooltip text="Sleep om te verplaatsen" location="bottom" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-drag-vertical" class="drag-handle-center"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <div class="dashboard-card-remove">
                    <v-tooltip text="Verwijderen" location="start" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-close" class="remove-btn-right" @click="removeWidget(widgetKey)"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <data-availability v-if="token" :token="token" :compact="true"></data-availability>
                </article>

                <article v-if="widgetKey === 'BuildingTypeOverview'" class="widget dashboard-card dashboard-card--small dashboard-card-wrapper" :class="{ 'is-dragging': draggedIndex === index }" draggable="true" @dragstart="handleCardDragStart($event, index)" @dragover="handleCardDragOver($event)" @drop="handleCardDrop($event, index)" @dragend="handleCardDragEnd">
                  <div class="dashboard-card-overlay">
                    <v-tooltip text="Sleep om te verplaatsen" location="bottom" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-drag-vertical" class="drag-handle-center"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <div class="dashboard-card-remove">
                    <v-tooltip text="Verwijderen" location="start" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-close" class="remove-btn-right" @click="removeWidget(widgetKey)"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <complex-functions v-if="token" :token="token" :compact="true"></complex-functions>
                </article>

                <article v-if="widgetKey === 'EnergyEfficiencyHomes' || widgetKey === 'EnergyEfficiency'" class="widget dashboard-card dashboard-card--small dashboard-card-wrapper" :class="{ 'is-dragging': draggedIndex === index }" draggable="true" @dragstart="handleCardDragStart($event, index)" @dragover="handleCardDragOver($event)" @drop="handleCardDrop($event, index)" @dragend="handleCardDragEnd">
                  <div class="dashboard-card-overlay">
                    <v-tooltip text="Sleep om te verplaatsen" location="bottom" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-drag-vertical" class="drag-handle-center"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <div class="dashboard-card-remove">
                    <v-tooltip text="Verwijderen" location="start" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-close" class="remove-btn-right" @click="removeWidget(widgetKey)"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <sustainability-radar v-if="userStore.currentPartyReference" :partyReference="userStore.currentPartyReference"></sustainability-radar>
                </article>

                <article v-if="widgetKey === 'EnergyLabelsOverview'" class="widget dashboard-card dashboard-card--large dashboard-card-wrapper" :class="{ 'is-dragging': draggedIndex === index }" draggable="true" @dragstart="handleCardDragStart($event, index)" @dragover="handleCardDragOver($event)" @drop="handleCardDrop($event, index)" @dragend="handleCardDragEnd">
                  <div class="dashboard-card-overlay">
                    <v-tooltip text="Sleep om te verplaatsen" location="bottom" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-drag-vertical" class="drag-handle-center"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <div class="dashboard-card-remove">
                    <v-tooltip text="Verwijderen" location="start" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-close" class="remove-btn-right" @click="removeWidget(widgetKey)"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <building-energylabel-status v-if="token" :token="token" :compact="true" :partyType="userStore.currentPartyType" labelSource="EPOnline"></building-energylabel-status>
                </article>

                <article v-if="widgetKey === 'MeasuresOverview'" class="widget dashboard-card dashboard-card--large dashboard-card-wrapper" :class="{ 'is-dragging': draggedIndex === index }" draggable="true" @dragstart="handleCardDragStart($event, index)" @dragover="handleCardDragOver($event)" @drop="handleCardDrop($event, index)" @dragend="handleCardDragEnd">
                  <div class="dashboard-card-overlay">
                    <v-tooltip text="Sleep om te verplaatsen" location="bottom" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-drag-vertical" class="drag-handle-center"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <div class="dashboard-card-remove">
                    <v-tooltip text="Verwijderen" location="start" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-close" class="remove-btn-right" @click="removeWidget(widgetKey)"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <building-measures v-if="token" :token="token" :certified="false" :refDate="refDate">
                    <template #header>
                      <h2>Maatregelen</h2>
                    </template>
                    <template #infotext>
                      <p>In dit overzicht wordt getoond op basis van al uw complexen wat de voortgang is.</p>
                    </template>
                    <template #nomeasurestext>
                      <p>Er zijn nog geen maatregelen aan dit project toegevoegd</p>
                    </template>
                  </building-measures>
                </article>

                <article v-if="widgetKey === 'HeatingTypeOverview'" class="widget dashboard-card dashboard-card--small dashboard-card-wrapper" :class="{ 'is-dragging': draggedIndex === index }" draggable="true" @dragstart="handleCardDragStart($event, index)" @dragover="handleCardDragOver($event)" @drop="handleCardDrop($event, index)" @dragend="handleCardDragEnd">
                  <div class="dashboard-card-overlay">
                    <v-tooltip text="Sleep om te verplaatsen" location="bottom" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-drag-vertical" class="drag-handle-center"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <div class="dashboard-card-remove">
                    <v-tooltip text="Verwijderen" location="start" :disabled="tooltipsDisabled">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-close" class="remove-btn-right" @click="removeWidget(widgetKey)"></v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <homesHeatingTypes v-if="token" :token="token"></homesHeatingTypes>
                </article>
              </template>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </BasePage>
</template>

<style scoped lang="scss">
.dashboard-card-wrapper {
  opacity: 1;
  transition: opacity 0.2s;

  &.is-dragging {
    opacity: 0.5;
  }

  &:hover .dashboard-card-overlay,
  &:hover .dashboard-card-remove {
    visibility: visible;
  }
}

.dashboard-card-overlay {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  visibility: hidden;
  z-index: 10;
  transition: visibility 0.2s;
  pointer-events: none;

  .drag-handle-center {
    cursor: grab;
    color: rgba(0, 0, 0, 0.54);
    font-size: 20px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 4px;
    padding: 6px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    pointer-events: auto;

    &:active {
      cursor: grabbing;
    }
  }
}

.dashboard-card-remove {
  position: absolute;
  top: 12px;
  right: 12px;
  visibility: hidden;
  z-index: 10;
  transition: visibility 0.2s;
  pointer-events: none;

  .remove-btn-right {
    cursor: pointer;
    color: #999;
    font-size: 20px;
    transition: color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;

    &:hover {
      color: #d32f2f;
    }
  }
}

.dashboard-card--net-heat :deep(.app-paris-proof-sidebar--compact > header) {
  padding-top: 13px;
}
</style>

